import NoDataForSelection from 'components/atoms/NoDataForSelection';
import TileButton, { tileDataProp } from 'components/atoms/TileButton';
import MainSection from 'components/template/BumpReport/MainSection';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getConfig } from 'utils/helperMethods';
import { commonClassName, commonHeaderClass } from './PromoScorecard';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    BUMP_REPORT_ABS_TAB_CLICKED,
    BUMP_REPORT_MAIN_TAB_CLICKED,
    BUMP_REPORT_VALUE_TAB_CLICKED,
} from 'utils/mixpanelEventNames';
import { Switch } from 'antd';

// export const groupByKey = 'groupBy';
// const bumpReportTileData = [
//     { key: 'increments', label: 'Increments' },
//     { key: 'promo', label: 'Promo' },
//     { key: groupByKey, label: 'Group By' },
// ];

const BumpReport = () => {
    const { appConfig } = useContext(UserContext);
    const bumpReportConfig = getConfig('bumpReport', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const volGroupByOptionsMapping = bumpReportConfig.volGroupByOptionsMapping;
    const { absTileData, volTileData, bumpReportTileData, groupByKey } = bumpReportConfig;
    const { filterNullError } = useContext(FilterContextData);
    const filterContext = useContext(FilterContextData);
    const [bumpReportTile, setBumpReportTile] = useState(bumpReportTileData[0]);
    const [absTile, setAbsTile] = useState(absTileData[0]);
    const [volTile, setVolTile] = useState(volTileData[0]);
    const bumpChartY2AxisOptions = bumpReportConfig.bumpChartY2AxisOptions;
    const [loading, setLoading] = useState(false);
    const [groupByOptionSelected, setGroupByOptionSelected] = useState(volGroupByOptionsMapping[volTile.key][0]);
    const [optionSelected, setOptionSelected] = useState([bumpChartY2AxisOptions[volTile.key][0].value]);
    const [showAllLabels, setShowAllLabels] = useState(false);

    /**
     * Handles volTile change by preserving existing selections if they exist in new options,
     * otherwise sets the default value
     */
    const handleVolTileChange = useCallback(
        (item: tileDataProp) => {
            setGroupByOptionSelected(volGroupByOptionsMapping[item.key][0]);

            // Get new options for the selected volTile
            const newOptions = bumpChartY2AxisOptions[item.key] || [];
            const defaultValue = newOptions[0]?.value;

            //if no options available, just update volTile and return
            if (newOptions.length === 0 || !defaultValue) {
                setVolTile(item);
                return;
            }

            // Normalize current selection to array of values (handle both object and value formats)
            const normalizedSelection = Array.isArray(optionSelected)
                ? optionSelected.map((sel: any) => (typeof sel === 'object' && sel !== null ? sel.value : sel))
                : [];

            const newOptionValues = new Set(newOptions.map((opt: any) => opt.value));

            // Filter to only selections that exist in the new options
            const preservedSelections = normalizedSelection.filter((val: any) => newOptionValues.has(val));

            // Use preserved selections if available, otherwise use default
            const newSelection = preservedSelections.length > 0 ? preservedSelections : [defaultValue];

            setOptionSelected(newSelection);
            setVolTile(item);

            // Mixpanel event
            trackEventMixpanel(BUMP_REPORT_VALUE_TAB_CLICKED, {
                tabName: item.label,
            });
        },
        [optionSelected, bumpChartY2AxisOptions, volGroupByOptionsMapping],
    );

    const tileDataMemoized = useMemo(() => {
        return {
            absTile,
            volTile,
            bumpReportTile,
        };
    }, [absTile, volTile, bumpReportTile]);
    return (
        <section className={`${commonClassName} bg-white`}>
            {filterNullError ? (
                <NoDataForSelection />
            ) : (
                <div>
                    <div className="flex justify-between items-center">
                        <div className="flex justify-start items-center gap-4">
                            <h2 className={commonHeaderClass}>{bumpReportConfig?.mainHeader}</h2>
                            <TileButton
                                disabled={loading}
                                data={bumpReportTileData}
                                activeKey={bumpReportTile.key}
                                onClick={(item: any) => {
                                    setBumpReportTile(item);
                                    //mixpanel event
                                    trackEventMixpanel(BUMP_REPORT_MAIN_TAB_CLICKED, {
                                        tabName: item.label,
                                    });
                                }}
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center">
                                <span className="font-xs text-blackAlt mr-1">Show Label</span>
                                <Switch onChange={(val) => setShowAllLabels(val)} checked={showAllLabels} />
                            </div>
                            <TileButton
                                disabled={loading}
                                data={absTileData}
                                activeKey={absTile.key}
                                onClick={(item: tileDataProp) => {
                                    setAbsTile(item);
                                    //mixpanel event
                                    trackEventMixpanel(BUMP_REPORT_ABS_TAB_CLICKED, {
                                        tabName: item.label,
                                    });
                                }}
                            />
                            <TileButton
                                disabled={loading}
                                data={volTileData}
                                activeKey={volTile.key}
                                onClick={handleVolTileChange}
                            />
                        </div>
                    </div>
                    <MainSection
                        showAllLabels={showAllLabels}
                        setLoading={setLoading}
                        groupByOptionSelected={groupByOptionSelected}
                        setGroupByOptionSelected={setGroupByOptionSelected}
                        optionSelected={optionSelected}
                        setOptionSelected={setOptionSelected}
                        tileData={tileDataMemoized}
                    />
                </div>
            )}
        </section>
    );
};

export default BumpReport;
